// Guest Check-In Component for AAR Guest Management System
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { checkInGuest, fetchParticipants, fetchTodayCheckIns, fetchMissingPagers, fetchPagerSettings, fetchAssignedPagerNumbers, fetchUnreturnedPagerNumbers, deleteCheckIn } from '../firebase/firestoreService';
import { getCurrentUser, getUserRole } from '../firebase/authService';
import { getAllServiceTypes, canAccessGuestProfiles } from '../utils/roles';
import { logAction, logError, LOG_CATEGORY, LOG_STATUS } from '../firebase/activityLog';
import { useAppBusy } from '../utils/appUpdate';
import { getCurrentServiceType } from '../utils/dateUtils';
import EditGuestModal from './EditGuestModal';
import WebcamCaptureModal from './WebcamCaptureModal';
import placeholderAvatar from '../assets/placeholder-avatar.svg';
import '../styles/CheckIn.css';
import { readOnlyProps } from '../utils/viewAs';
import '../styles/ConfirmDialog.css';

// Format phone number to (XXX) XXX-XXXX format
const formatPhoneNumber = (phone) => {
  if (!phone) return '';

  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');

  // Format based on length
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else if (cleaned.length === 11 && cleaned[0] === '1') {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }

  // Return original if format doesn't match
  return phone;
};

const CheckIn = () => {
  const ro = readOnlyProps(); // disable write controls while viewing as someone
  const navigate = useNavigate();
  const [guests, setGuests] = useState([]);
  const [filteredGuests, setFilteredGuests] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [serviceType, setServiceType] = useState('');
  const [pagerNumber, setPagerNumber] = useState('');
  const [expectedPickupTime, setExpectedPickupTime] = useState('');
  const expectedPickupAutoFilledRef = useRef(false);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showNewGuestForm, setShowNewGuestForm] = useState(false);
  const [checkedInGuestIds, setCheckedInGuestIds] = useState(new Set());
  const [todayCheckIns, setTodayCheckIns] = useState([]);
  const [newGuest, setNewGuest] = useState({
    firstName: '',
    lastName: '',
    gender: '',
    birthday: '',
    homePhone: '',
    mobilePhone: '',
    email: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    emergencyContactName: '',
    emergencyContactPhone: ''
  });
  const [showPhotoCapture, setShowPhotoCapture] = useState(false);
  const [selectedGuestForPhoto, setSelectedGuestForPhoto] = useState(null);
  const [guestImageDimensions, setGuestImageDimensions] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [newGuestPhoto, setNewGuestPhoto] = useState(null);
  const [newGuestPhotoUrl, setNewGuestPhotoUrl] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCheckedInConfirm, setShowCheckedInConfirm] = useState(false);
  const [checkedInName, setCheckedInName] = useState('');
  // Guest just checked in during this session. Suppresses the "Already Checked
  // In" banner while the success modal is up (the selected-guest card stays
  // mounted until the modal is dismissed, and loadTodayCheckIns() has by then
  // added this guest's fresh record).
  const [justCheckedInGuestId, setJustCheckedInGuestId] = useState(null);
  // Duplicate-guest confirmation gate: existing participants whose name matches
  // the new-guest form. Non-empty while the confirm dialog is open.
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
  const [duplicateMatches, setDuplicateMatches] = useState([]);
  // Re-entrancy guard for the check-in write — blocks a second submit before
  // React has re-rendered the disabled button (rapid double-tap / Enter key).
  const submitInProgressRef = useRef(false);

  // Hold off the idle auto-reload while a check-in is in progress or a guest
  // is selected but not yet submitted — a reload there would discard the
  // operator's in-progress entry.
  useAppBusy(
    'guest-checkin',
    submitting || selectedGuest !== null || showNewGuestForm || showCheckedInConfirm
  );
  const [deleteCheckInTarget, setDeleteCheckInTarget] = useState(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [showWebcamCapture, setShowWebcamCapture] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [showGuestDropdown, setShowGuestDropdown] = useState(false);
  const [availablePagers, setAvailablePagers] = useState([]);
  const user = getCurrentUser();

  // Load user role
  useEffect(() => {
    const loadUserRole = async () => {
      if (user) {
        const role = await getUserRole(user.uid);
        setUserRole(role);
      }
    };
    loadUserRole();
  }, [user]);

  // Scroll to top when error or success message appears
  useEffect(() => {
    if (error || success) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [error, success]);

  // Maintain a preview object-URL that mirrors the new-guest photo File.
  // Revoke on change/unmount so we don't leak blob URLs.
  useEffect(() => {
    if (!newGuestPhoto) {
      setNewGuestPhotoUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(newGuestPhoto);
    setNewGuestPhotoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [newGuestPhoto]);

  useEffect(() => {
    loadGuests();
    loadTodayCheckIns();
    loadAvailablePagers();

    // Auto-detect service type based on current time
    const detectedService = getCurrentServiceType();
    if (detectedService) {
      setServiceType(detectedService);
    }
  }, []);

  useEffect(() => {
    // Filter logic:
    // - If searching: show all guests matching search (so you can find anyone to check in)
    // - If not searching: show only checked-in guests
    let filtered;

    if (searchTerm.trim() !== '') {
      // When searching, search through ALL guests
      const term = searchTerm.toLowerCase();
      filtered = guests.filter(guest =>
        guest.firstName.toLowerCase().includes(term) ||
        guest.lastName.toLowerCase().includes(term) ||
        `${guest.firstName} ${guest.lastName}`.toLowerCase().includes(term)
      );
    } else {
      // When not searching, show only checked-in guests
      filtered = guests.filter(guest => checkedInGuestIds.has(guest.id));
    }

    setFilteredGuests(filtered);
  }, [searchTerm, guests, checkedInGuestIds]);

  // Get all guests sorted alphabetically for dropdown
  const getSortedGuests = () => {
    return [...guests].sort((a, b) => {
      const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
      const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  };

  // Get filtered guests for dropdown based on search term
  const getDropdownGuests = () => {
    const sorted = getSortedGuests();
    if (searchTerm.trim() === '') {
      return sorted;
    }
    const term = searchTerm.toLowerCase();
    return sorted.filter(guest =>
      guest.firstName.toLowerCase().includes(term) ||
      guest.lastName.toLowerCase().includes(term) ||
      `${guest.firstName} ${guest.lastName}`.toLowerCase().includes(term)
    );
  };

  const handleSearchClick = () => {
    setShowGuestDropdown(true);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setShowGuestDropdown(true);
  };

  const handleDropdownGuestSelect = (guest) => {
    // Select the guest. If they're already checked in, the selected-guest card
    // shows a clear "Already Checked In" banner with a Go to Check-Out action
    // (rather than silently jumping to the check-out page).
    setSelectedGuest(guest);
    setShowNewGuestForm(false);
    setShowGuestDropdown(false);
    setSearchTerm('');
    setError('');
    setJustCheckedInGuestId(null);
    // Scroll to top to show selected guest and check-in form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const loadGuests = async () => {
    try {
      setLoading(true);
      setError('');
      // Fetch participants from Firestore cache (synced from CCB)
      console.log('Fetching participants from Firestore cache...');
      const participantList = await fetchParticipants('1269');
      console.log('Received participants:', participantList);

      if (!participantList || participantList.length === 0) {
        setError('No participants found. Please wait for the daily sync or contact an administrator.');
        setGuests([]);
        setFilteredGuests([]);
      } else {
        setGuests(participantList);
        console.log(`Successfully loaded ${participantList.length} participants`);
      }
    } catch (err) {
      console.error('Error loading participants:', err);
      console.error('Error details:', err.message, err.code);
      setError(`Failed to load guest list: ${err.message || 'Please try again.'}`);
      setGuests([]);
      setFilteredGuests([]);
    } finally {
      setLoading(false);
    }
  };

  const loadTodayCheckIns = async () => {
    try {
      console.log('Fetching today\'s check-ins...');
      const checkIns = await fetchTodayCheckIns();
      console.log('Received check-ins:', checkIns);

      // Store full check-in records
      setTodayCheckIns(checkIns);

      // Extract unique guest IDs from today's check-ins (only those still checked in)
      const guestIds = new Set(
        checkIns
          .filter(checkIn => checkIn.status === 'checked_in')
          .map(checkIn => checkIn.guestId)
      );
      setCheckedInGuestIds(guestIds);
      console.log(`Found ${guestIds.size} unique guests checked in today`);
    } catch (err) {
      console.error('Error loading today\'s check-ins:', err);
      // Don't show error to user, just log it
      // Still allow the component to function even if check-ins can't be loaded
    }
  };

  const loadAvailablePagers = async () => {
    try {
      const [settings, missing, assigned, unreturned] = await Promise.all([
        fetchPagerSettings(),
        fetchMissingPagers(),
        fetchAssignedPagerNumbers(),
        fetchUnreturnedPagerNumbers()
      ]);
      const total = settings.totalPagers || 0;
      const missingSet = new Set(missing.map(p => String(p.pagerNumber)));
      const available = [];
      for (let i = 1; i <= total; i++) {
        const num = String(i);
        // A pager is available only if it is not flagged missing, not currently
        // assigned to an open check-in, and not still out from a prior check-out
        // that was never physically returned.
        if (!missingSet.has(num) && !assigned.has(num) && !unreturned.has(num)) {
          available.push(num);
        }
      }
      setAvailablePagers(available);
    } catch (err) {
      console.error('Error loading available pagers:', err);
    }
  };

  // Get the check-in record for a guest
  const getGuestCheckInRecord = (guestId) => {
    return todayCheckIns.find(
      checkIn => checkIn.guestId === guestId && checkIn.status === 'checked_in'
    );
  };

  // Latest check-in record for this guest from today (any status). Used by the
  // sys_admin delete-test-checkin button so freshly-checked-out test records
  // can also be cleaned up without leaving the page.
  const getLatestTodayCheckIn = (guestId) => {
    const matches = todayCheckIns.filter((c) => c.guestId === guestId);
    if (matches.length === 0) return null;
    return matches.reduce((latest, c) => {
      const lt = latest?.checkInTime?.toMillis?.() ?? 0;
      const ct = c?.checkInTime?.toMillis?.() ?? 0;
      return ct > lt ? c : latest;
    }, null);
  };

  // Sys-admin only: delete the most-recent check-in for a guest. Used to clean
  // up test records. The button is gated in the UI by userRole === 'sys_admin';
  // Firestore rules enforce the same restriction server-side.
  const handleDeleteCheckIn = async () => {
    if (!deleteCheckInTarget) return;
    try {
      setDeleteInProgress(true);
      await deleteCheckIn(deleteCheckInTarget.checkInId);
      setDeleteCheckInTarget(null);
      await loadTodayCheckIns();
      await loadAvailablePagers();
    } catch (err) {
      console.error('Delete check-in error:', err);
      setError('Failed to delete check-in record. Please try again.');
      setDeleteCheckInTarget(null);
    } finally {
      setDeleteInProgress(false);
    }
  };

  const handleSelectGuest = (guest) => {
    // Select the guest. The selected-guest card surfaces an "Already Checked In"
    // banner + Go to Check-Out action when applicable, so the status is obvious.
    setSelectedGuest(guest);
    setShowNewGuestForm(false);
    setError('');
    setJustCheckedInGuestId(null);
    // Scroll to top to show selected guest and check-in form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetNewGuestForm = () => {
    setNewGuest({
      firstName: '',
      lastName: '',
      gender: '',
      birthday: '',
      homePhone: '',
      mobilePhone: '',
      email: '',
      street: '',
      city: '',
      state: '',
      zip: '',
      emergencyContactName: '',
      emergencyContactPhone: ''
    });
    setNewGuestPhoto(null);
  };

  // Validation functions
  const validateEmail = (email) => {
    if (!email) return true; // Optional field
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateName = (name) => {
    if (!name) return true;
    // Only letters, spaces, hyphens, and apostrophes
    const nameRegex = /^[a-zA-Z\s'-]+$/;
    // Check if it looks like email or phone
    const hasAtSymbol = name.includes('@');
    const hasMultipleDigits = (name.match(/\d/g) || []).length > 2;
    return nameRegex.test(name) && !hasAtSymbol && !hasMultipleDigits;
  };

  const validatePhone = (phone) => {
    if (!phone) return true; // Optional field
    // Remove all non-digit characters
    const digitsOnly = phone.replace(/\D/g, '');
    // Must be exactly 10 digits
    return digitsOnly.length === 10;
  };

  const formatPhoneInput = (value) => {
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, '');
    // Limit to 10 digits
    const limited = digitsOnly.slice(0, 10);
    // Format as (XXX) XXX-XXXX
    if (limited.length <= 3) return limited;
    if (limited.length <= 6) return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
    return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
  };

  const validateState = (state) => {
    if (!state) return true; // Optional field
    // Must be exactly 2 letters
    return /^[A-Z]{2}$/.test(state.toUpperCase());
  };

  const validateZip = (zip) => {
    if (!zip) return true; // Optional field
    // Must be 5 digits or 5+4 format
    return /^\d{5}(-\d{4})?$/.test(zip);
  };

  // Normalize a name for duplicate comparison: trim, lowercase, collapse
  // internal whitespace. Keeps hyphens/apostrophes so "Anne-Marie" stays intact.
  const normalizeName = (s) => (s || '').trim().toLowerCase().replace(/\s+/g, ' ');

  // Existing participants whose first AND last name match the given values.
  // Runs against the already-loaded guest list (no extra fetch).
  const findMatchingGuests = (firstName, lastName) => {
    const f = normalizeName(firstName);
    const l = normalizeName(lastName);
    if (!f || !l) return [];
    return guests.filter(
      (g) => normalizeName(g.firstName) === f && normalizeName(g.lastName) === l
    );
  };

  // Switch from the new-guest form to an existing matched participant. This
  // re-engages the normal check-in flow (and the server-side double check-in
  // guard + "Already Checked In" banner if they're already checked in).
  const handleUseExistingGuest = (guest) => {
    setShowDuplicateConfirm(false);
    setDuplicateMatches([]);
    setShowNewGuestForm(false);
    resetNewGuestForm();
    setSelectedGuest(guest);
    setJustCheckedInGuestId(null);
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleShowNewGuestForm = () => {
    setShowNewGuestForm(true);
    setSelectedGuest(null);
    resetNewGuestForm();
    setError('');
  };

  const handleCheckIn = async (e) => {
    e.preventDefault();

    if (!selectedGuest && !showNewGuestForm) {
      setError('Please select a guest or add a new guest.');
      return;
    }

    if (!serviceType) {
      setError('Please select a service type.');
      return;
    }

    // Validate new guest form fields (CCB required fields)
    if (showNewGuestForm) {
      // Required fields
      if (!newGuest.firstName || !newGuest.lastName) {
        setError('Please enter guest first and last name.');
        return;
      }
      if (!newGuest.gender) {
        setError('Please select a gender.');
        return;
      }
      if (!newGuest.birthday) {
        setError('Please enter date of birth.');
        return;
      }

      // Field format validation
      if (!validateName(newGuest.firstName)) {
        setError('First name can only contain letters, spaces, hyphens, and apostrophes. No numbers or special characters.');
        return;
      }
      if (!validateName(newGuest.lastName)) {
        setError('Last name can only contain letters, spaces, hyphens, and apostrophes. No numbers or special characters.');
        return;
      }
      if (!validateEmail(newGuest.email)) {
        setError('Please enter a valid email address.');
        return;
      }
      if (newGuest.homePhone && !validatePhone(newGuest.homePhone)) {
        setError('Home phone must be a valid 10-digit phone number.');
        return;
      }
      if (newGuest.mobilePhone && !validatePhone(newGuest.mobilePhone)) {
        setError('Mobile phone must be a valid 10-digit phone number.');
        return;
      }
      if (newGuest.state && !validateState(newGuest.state)) {
        setError('State must be a valid 2-letter state code (e.g., NY, CA).');
        return;
      }
      if (newGuest.zip && !validateZip(newGuest.zip)) {
        setError('Zip code must be 5 digits or 5+4 format (e.g., 12345 or 12345-6789).');
        return;
      }
      if (newGuest.emergencyContactName && !validateName(newGuest.emergencyContactName)) {
        setError('Emergency contact name can only contain letters, spaces, hyphens, and apostrophes.');
        return;
      }
      if (newGuest.emergencyContactPhone && !validatePhone(newGuest.emergencyContactPhone)) {
        setError('Emergency contact phone must be a valid 10-digit phone number.');
        return;
      }
    }

    // Duplicate-guest gate: for a NEW guest, if the entered name matches an
    // existing participant, don't silently create a duplicate profile (and a
    // duplicate CCB profile). Make the operator confirm or switch to the
    // existing guest. This also closes the double check-in hole where "Add New
    // Guest" bypasses the server-side activeCheckIns guard (which keys on id).
    if (showNewGuestForm) {
      const matches = findMatchingGuests(newGuest.firstName, newGuest.lastName);
      if (matches.length > 0) {
        setDuplicateMatches(matches);
        setShowDuplicateConfirm(true);
        return;
      }
    }

    await submitCheckIn();
  };

  // Performs the actual check-in write. Separated from handleCheckIn so the
  // duplicate-confirm dialog's "Create anyway" path can call it directly.
  // Guarded against re-entrancy (rapid double-submit) by submitInProgressRef,
  // on top of the disabled submit button.
  const submitCheckIn = async () => {
    if (submitInProgressRef.current) return;
    submitInProgressRef.current = true;
    try {
      setSubmitting(true);
      setError('');

      const checkInData = {
        guestId: selectedGuest?.id || 'new',
        guestFirstName: selectedGuest?.firstName || newGuest.firstName,
        guestLastName: selectedGuest?.lastName || newGuest.lastName,
        serviceType: serviceType,
        checkInBy: user.uid,
        checkInByName: user.displayName || user.email,
        checkInNotes: notes,
        pagerNumber: pagerNumber,
        expectedPickupTime: expectedPickupTime
      };

      // Add new guest details if this is a new guest
      if (showNewGuestForm) {
        checkInData.newGuestDetails = {
          gender: newGuest.gender,
          birthday: newGuest.birthday,
          homePhone: newGuest.homePhone || '',
          mobilePhone: newGuest.mobilePhone || '',
          email: newGuest.email,
          street: newGuest.street || '',
          city: newGuest.city || '',
          state: newGuest.state || '',
          zip: newGuest.zip || '',
          emergencyContactName: newGuest.emergencyContactName || '',
          emergencyContactPhone: newGuest.emergencyContactPhone || ''
        };

        // Add photo if captured
        if (newGuestPhoto) {
          checkInData.newGuestPhoto = newGuestPhoto;
        }
      }

      await checkInGuest(checkInData);

      // Mark this guest as just-checked-in BEFORE reloading, so the reload
      // (which adds their record) doesn't flip the still-mounted selected-guest
      // card into the "Already Checked In" state behind the success modal.
      setJustCheckedInGuestId(selectedGuest?.id || null);

      // Reload today's check-ins and available pagers
      await loadTodayCheckIns();
      await loadAvailablePagers();

      // If it's a new guest, reload the guests list to include them
      if (showNewGuestForm) {
        await loadGuests();
      }

      // Show success modal — form reset happens when user dismisses it
      setCheckedInName(`${checkInData.guestFirstName} ${checkInData.guestLastName}`);
      setShowCheckedInConfirm(true);

      logAction(
        LOG_CATEGORY.GUEST_CHECKIN,
        'guest_check_in',
        LOG_STATUS.SUCCESS,
        `Checked in ${checkInData.guestFirstName} ${checkInData.guestLastName}`,
        {
          guestId: checkInData.guestId || null,
          serviceType: checkInData.serviceType || null,
          isNewGuest: !!showNewGuestForm,
          pagerNumber: checkInData.pagerNumber || null,
        }
      );

    } catch (err) {
      console.error('Check-in error:', err);
      // Guest is already checked in (validated fresh against the server, or a
      // concurrent double-submit lost the race). Refresh state and send the
      // operator to the existing open check-in's check-out page.
      if (err?.code === 'ALREADY_CHECKED_IN') {
        await loadTodayCheckIns();
        const name = `${selectedGuest?.firstName || newGuest.firstName} ${selectedGuest?.lastName || newGuest.lastName}`;
        logAction(
          LOG_CATEGORY.GUEST_CHECKIN,
          'guest_check_in_duplicate_blocked',
          LOG_STATUS.INFO,
          `${name} already had an open check-in`,
          { guestId: selectedGuest?.id || null, existingCheckInId: err.existingCheckInId || null }
        );
        if (err.existingCheckInId) {
          setError(`${name} is already checked in. Taking you to their check-out...`);
          navigate(`/check-out/${err.existingCheckInId}`);
        } else {
          setError(`${name} is already checked in.`);
        }
        return;
      }
      setError('Failed to check in guest. Please try again.');
      logError(
        LOG_CATEGORY.GUEST_CHECKIN,
        'guest_check_in_failed',
        err,
        'Guest check-in failed',
        {
          guestId: selectedGuest?.id || null,
          isNewGuest: !!showNewGuestForm,
        }
      );
    } finally {
      setSubmitting(false);
      submitInProgressRef.current = false;
    }
  };

  const proceedWithDuplicateCreate = async () => {
    setShowDuplicateConfirm(false);
    setDuplicateMatches([]);
    await submitCheckIn();
  };

  const dismissCheckedInConfirm = () => {
    setShowCheckedInConfirm(false);
    setCheckedInName('');
    setJustCheckedInGuestId(null);
    setSelectedGuest(null);
    setShowNewGuestForm(false);
    resetNewGuestForm();
    setPagerNumber('');
    setExpectedPickupTime('');
    setNotes('');
    setSearchTerm('');
  };

  const handleImageLoad = (guest, event) => {
    const img = event.target;
    setGuestImageDimensions(prev => ({
      ...prev,
      [guest.id]: {
        width: img.naturalWidth,
        height: img.naturalHeight
      }
    }));
  };

  const isAvatarPlaceholder = (guest) => {
    const dimensions = guestImageDimensions[guest.id];
    if (!dimensions) return false; // Image hasn't loaded yet, show it
    // Avatar placeholders are 600x400px
    return dimensions.width === 600 && dimensions.height === 400;
  };

  const handleAddPhoto = (guest) => {
    setSelectedGuestForPhoto(guest);
    setShowPhotoCapture(true);
  };

  // True when the device has a precise, hover-capable pointer (mouse/trackpad).
  // Used to route the "Capture Photo" button to an in-page webcam modal on
  // desktops/laptops, while keeping the native camera input for phones/tablets.
  const isDesktopPointer = () =>
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches &&
    !!navigator.mediaDevices?.getUserMedia;

  // Click handler shared by the new-guest "Capture Photo" label. On desktop,
  // open the webcam modal (and prevent the native file dialog); on mobile,
  // let the label's default behavior trigger the file input.
  const handleCapturePhotoClick = (e) => {
    if (isDesktopPointer()) {
      e.preventDefault();
      setShowWebcamCapture(true);
    }
  };

  // Process a File (from either webcam or native input) for the new-guest form.
  const acceptNewGuestPhotoFile = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setError('Photo size must be less than 10MB.');
      return;
    }
    try {
      const resized = await resizeImage(file, 800, 800, 0.85);
      setNewGuestPhoto(resized);
      setError('');
    } catch (err) {
      setError('Failed to process photo. Please try another image.');
    }
  };

  const resizeImage = (file, maxWidth = 800, maxHeight = 800, quality = 0.85) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions while maintaining aspect ratio
          if (width > height) {
            if (width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = Math.round((width * maxHeight) / height);
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to blob with JPEG compression
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(new File([blob], 'photo.jpg', { type: 'image/jpeg' }));
              } else {
                reject(new Error('Failed to compress image'));
              }
            },
            'image/jpeg',
            quality
          );
        };
        img.onerror = () => reject(new Error('Failed to load image'));
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
    });
  };

  const handlePhotoCapture = async (photoFile) => {
    if (!photoFile || !selectedGuestForPhoto) return;

    try {
      setError('');
      setSuccess('Processing photo...');

      // Resize and compress image (max 800x800, JPEG, 85% quality)
      const resizedPhoto = await resizeImage(photoFile, 800, 800, 0.85);

      const { uploadGuestPhoto } = await import('../firebase/firestoreService');

      // Upload to Firebase Storage in participant-images folder (same as CCB format)
      const photoURL = await uploadGuestPhoto(selectedGuestForPhoto.id, resizedPhoto);

      // Update guest in local state
      setGuests(guests.map(g =>
        g.id === selectedGuestForPhoto.id ? { ...g, image: photoURL } : g
      ));

      setSuccess('Photo uploaded successfully!');
      setShowPhotoCapture(false);
      setSelectedGuestForPhoto(null);

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Photo upload error:', err);
      setError('Failed to upload photo. Please try again.');
    }
  };

  const handleRemovePhoto = async () => {
    try {
      setError('');
      setSuccess('Removing photo...');
      setShowDeleteConfirm(false);
      setShowPhotoCapture(false);

      const { removeGuestPhoto } = await import('../firebase/firestoreService');

      // Remove from Firebase Storage and Firestore
      await removeGuestPhoto(selectedGuestForPhoto.id);

      // Update guest in local state to remove image
      setGuests(guests.map(g =>
        g.id === selectedGuestForPhoto.id ? { ...g, image: undefined, imageSource: undefined } : g
      ));

      // If this is the selected guest, update that too
      if (selectedGuest?.id === selectedGuestForPhoto.id) {
        setSelectedGuest({ ...selectedGuest, image: undefined, imageSource: undefined });
      }

      setSelectedGuestForPhoto(null);
      setSuccess('Photo removed successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Photo removal error:', err);
      setError('Failed to remove photo. Please try again.');
    }
  };


  const serviceTypes = getAllServiceTypes();

  // Open check-in record for the currently selected guest, if any. Drives the
  // "Already Checked In" banner and suppresses the check-in form so a duplicate
  // can't be created. Excludes the guest we just checked in this session so the
  // banner never flashes on a first-time check-in (see justCheckedInGuestId).
  const selectedGuestCheckIn =
    selectedGuest && selectedGuest.id !== justCheckedInGuestId
      ? getGuestCheckInRecord(selectedGuest.id)
      : null;

  // Live duplicate matches for the new-guest form (drives the inline warning).
  const newGuestMatches = showNewGuestForm
    ? findMatchingGuests(newGuest.firstName, newGuest.lastName)
    : [];

  // Format a check-in record's time for the banner (e.g. "10:24 AM").
  const formatCheckInClock = (record) => {
    if (!record?.checkInTime) return '';
    const d = record.checkInTime.toDate ? record.checkInTime.toDate() : new Date(record.checkInTime);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  if (loading) {
    return (
      <div className="loading"><div className="spinner"></div><p>Loading...</p></div>
    );
  }

  return (
    <div className="checkin-container">
      <div className="checkin-header">
        {(selectedGuest || showNewGuestForm) && (
          <button className="btn-back" onClick={() => {
            setSelectedGuest(null);
            setShowNewGuestForm(false);
            resetNewGuestForm();
            setPagerNumber('');
            setExpectedPickupTime('');
            setNotes('');
            setSearchTerm('');
          }}>
            ← Back to Guest List
          </button>
        )}
        <h1 className="checkin-title">Check In Guest</h1>
        <div className="header-spacer"></div>
      </div>

      {error && (
        <div className="message error-message">
          {error}
        </div>
      )}

      {success && (
        <div className="message success-message">
          {success}
        </div>
      )}

      <div className="checkin-content">
        {/* Guest Selection */}
        {!selectedGuest && !showNewGuestForm && (
          <div className="section">
            <div className="guest-selection-header">
              <h2>Select Guest</h2>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleShowNewGuestForm}
              >
                + Add New Guest
              </button>
            </div>

            <div className="search-box search-wrapper">
              <input
                type="text"
                placeholder="Search for a guest by name..."
                value={searchTerm}
                onChange={handleSearchChange}
                onClick={handleSearchClick}
                onBlur={() => setTimeout(() => setShowGuestDropdown(false), 200)}
                className="search-input"
              />
              {searchTerm && (
                <button
                  type="button"
                  className="search-clear-btn"
                  onClick={() => {
                    setSearchTerm('');
                    setShowGuestDropdown(false);
                  }}
                  aria-label="Clear search"
                >
                  ✕
                </button>
              )}
              {showGuestDropdown && getDropdownGuests().length > 0 && (
                <div className="search-dropdown">
                  {getDropdownGuests().map((guest, index) => (
                    <div
                      key={guest.id || index}
                      className="dropdown-item"
                      onClick={() => handleDropdownGuestSelect(guest)}
                    >
                      <div className="dropdown-guest-image">
                        {guest.image && !isAvatarPlaceholder(guest) ? (
                          <img src={guest.image} alt={`${guest.firstName} ${guest.lastName}`} />
                        ) : (
                          <img src={placeholderAvatar} alt={`${guest.firstName} ${guest.lastName}`} className="dropdown-guest-avatar-img" />
                        )}
                      </div>
                      <span className="dropdown-guest-name">
                        {guest.firstName} {guest.lastName}
                      </span>
                      {checkedInGuestIds.has(guest.id) && (
                        <span className="dropdown-checkin-badge">Checked In</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="guest-selection">
              {filteredGuests.length === 0 ? (
                <div className="no-results">
                  <p>No guests checked in today.</p>
                </div>
              ) : (
                <div className="guest-grid">
                  {filteredGuests.map(guest => {
                    const latestTodayCheckIn = userRole === 'sys_admin'
                      ? getLatestTodayCheckIn(guest.id)
                      : null;
                    return (
                    <div
                      key={guest.id}
                      className={`guest-option ${selectedGuest?.id === guest.id ? 'selected' : ''}`}
                      onClick={() => handleSelectGuest(guest)}
                    >
                      {checkedInGuestIds.has(guest.id) && (
                        <div className="checkin-status-dot"></div>
                      )}
                      {latestTodayCheckIn && (
                        <button
                          type="button"
                          className="delete-checkin-btn"
                          title="Delete this check-in (sys_admin only — for cleaning up test records)"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteCheckInTarget({
                              checkInId: latestTodayCheckIn.id,
                              guestName: `${guest.firstName} ${guest.lastName}`,
                              status: latestTodayCheckIn.status
                            });
                          }}
                        >
                          🗑️
                        </button>
                      )}
                      <div className="guest-option-image">
                        {guest.image && !isAvatarPlaceholder(guest) ? (
                          <img
                            src={guest.image}
                            alt={`${guest.firstName} ${guest.lastName}`}
                            onLoad={(e) => handleImageLoad(guest, e)}
                          />
                        ) : (
                          <>
                            {guest.image && (
                              <img
                                src={guest.image}
                                alt={`${guest.firstName} ${guest.lastName}`}
                                onLoad={(e) => handleImageLoad(guest, e)}
                                style={{ display: 'none' }}
                              />
                            )}
                            <img src={placeholderAvatar} alt={`${guest.firstName} ${guest.lastName}`} className="guest-avatar-placeholder-img" />
                            <button
                              className="add-photo-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddPhoto(guest);
                              }}
                              title="Add photo"
                            >
                              📷
                            </button>
                          </>
                        )}
                      </div>
                      <div className="guest-option-info">
                        <div className="guest-option-name">
                          {guest.firstName} {guest.lastName}
                        </div>
                        {guest.phone && (
                          <div className="guest-option-details">{formatPhoneNumber(guest.phone)}</div>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {selectedGuest && (
          <div className={`selected-guest-card ${selectedGuestCheckIn ? 'already-checked-in' : ''}`}>
            {selectedGuestCheckIn && (
              <div className="already-checked-in-banner">
                <div className="already-checked-in-info">
                  <span className="already-checked-in-icon">✓</span>
                  <div>
                    <div className="already-checked-in-title">Already Checked In</div>
                    <div className="already-checked-in-sub">
                      {selectedGuest.firstName} {selectedGuest.lastName} was checked in
                      {formatCheckInClock(selectedGuestCheckIn) && ` at ${formatCheckInClock(selectedGuestCheckIn)}`}
                      {selectedGuestCheckIn.pagerNumber && ` · Pager #${selectedGuestCheckIn.pagerNumber}`}.
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-primary already-checked-in-btn"
                  onClick={() => navigate(`/check-out/${selectedGuestCheckIn.id}`)}
                >
                  Go to Check-Out →
                </button>
              </div>
            )}
            <div className="selected-guest-content">
              <div className="selected-guest-image" style={{ position: 'relative' }}>
                {selectedGuest.image && !isAvatarPlaceholder(selectedGuest) ? (
                  <>
                    <img
                      src={selectedGuest.image}
                      alt={`${selectedGuest.firstName} ${selectedGuest.lastName}`}
                      onLoad={(e) => handleImageLoad(selectedGuest, e)}
                    />
                    <button
                      className="add-photo-btn-large"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddPhoto(selectedGuest);
                      }}
                      title="Update photo"
                    >
                      📷
                    </button>
                  </>
                ) : (
                  <>
                    {selectedGuest.image && (
                      <img
                        src={selectedGuest.image}
                        alt={`${selectedGuest.firstName} ${selectedGuest.lastName}`}
                        onLoad={(e) => handleImageLoad(selectedGuest, e)}
                        style={{ display: 'none' }}
                      />
                    )}
                    <img src={placeholderAvatar} alt={`${selectedGuest.firstName} ${selectedGuest.lastName}`} className="selected-guest-avatar-placeholder-img" />
                    <button
                      className="add-photo-btn-large"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddPhoto(selectedGuest);
                      }}
                      title="Add photo"
                    >
                      📷
                    </button>
                  </>
                )}
              </div>
              <div className="selected-guest-info">
                <div className="selected-guest-name-row">
                  <div className="selected-guest-name">
                    {selectedGuest.firstName} {selectedGuest.lastName}
                    {canAccessGuestProfiles(userRole) && (
                      <button
                        className="btn-edit-guest"
                        onClick={() => setShowEditModal(true)}
                        disabled={ro.disabled}
                        title={ro.title || 'Edit guest profile'}
                      >
                        ✏️
                      </button>
                    )}
                  </div>
                  <button
                    className="btn-change-guest"
                    onClick={() => {
                      setSelectedGuest(null);
                      setSearchTerm('');
                    }}
                  >
                    Change Guest
                  </button>
                </div>
                {selectedGuest.email && (
                  <div className="selected-guest-detail">
                    ✉️ {selectedGuest.email}
                  </div>
                )}
                {selectedGuest.phone && (
                  <div className="selected-guest-detail">
                    📱 {formatPhoneNumber(selectedGuest.phone)}
                  </div>
                )}
                {selectedGuest.emergencyContactName && (
                  <div className="selected-guest-detail">
                    🆘 Emergency Contact: {selectedGuest.emergencyContactName}
                    {selectedGuest.emergencyContactPhone && ` - ${formatPhoneNumber(selectedGuest.emergencyContactPhone)}`}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {showNewGuestForm && (
          <div className="section">
            <div className="new-guest-form">
              <div className="new-guest-header">
                <h3>New Guest Information</h3>
                <button
                  className="btn-change-guest"
                  onClick={() => {
                    setShowNewGuestForm(false);
                    resetNewGuestForm();
                  }}
                >
                  Cancel
                </button>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    value={newGuest.firstName}
                    onChange={(e) => setNewGuest({...newGuest, firstName: e.target.value})}
                    placeholder="Enter first name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    value={newGuest.lastName}
                    onChange={(e) => setNewGuest({...newGuest, lastName: e.target.value})}
                    placeholder="Enter last name"
                    required
                  />
                </div>
              </div>

              {newGuestMatches.length > 0 && (
                <div className="duplicate-warning">
                  <div className="duplicate-warning-head">
                    <span className="duplicate-warning-icon">⚠️</span>
                    <span>
                      {newGuestMatches.length === 1
                        ? 'A guest with this name already exists.'
                        : `${newGuestMatches.length} guests with this name already exist.`}{' '}
                      Select the existing profile instead of creating a duplicate:
                    </span>
                  </div>
                  <div className="duplicate-warning-list">
                    {newGuestMatches.map((g) => (
                      <button
                        key={g.id}
                        type="button"
                        className="duplicate-match-btn"
                        onClick={() => handleUseExistingGuest(g)}
                      >
                        <img
                          src={g.image && !isAvatarPlaceholder(g) ? g.image : placeholderAvatar}
                          alt={`${g.firstName} ${g.lastName}`}
                          className="duplicate-match-img"
                          onLoad={(e) => handleImageLoad(g, e)}
                        />
                        <span className="duplicate-match-name">{g.firstName} {g.lastName}</span>
                        {checkedInGuestIds.has(g.id) && (
                          <span className="dropdown-checkin-badge">Checked In</span>
                        )}
                        <span className="duplicate-match-cta">Use this profile →</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Gender *</label>
                  <select
                    value={newGuest.gender}
                    onChange={(e) => setNewGuest({...newGuest, gender: e.target.value})}
                    required
                  >
                    <option value="">Select gender</option>
                    <option value="m">Male</option>
                    <option value="f">Female</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Date of Birth *</label>
                  <input
                    type="date"
                    value={newGuest.birthday}
                    onChange={(e) => setNewGuest({...newGuest, birthday: e.target.value})}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Home Phone</label>
                  <input
                    type="tel"
                    value={newGuest.homePhone}
                    onChange={(e) => setNewGuest({...newGuest, homePhone: formatPhoneInput(e.target.value)})}
                    placeholder="(555) 555-5555"
                  />
                </div>
                <div className="form-group">
                  <label>Mobile Phone</label>
                  <input
                    type="tel"
                    value={newGuest.mobilePhone}
                    onChange={(e) => setNewGuest({...newGuest, mobilePhone: formatPhoneInput(e.target.value)})}
                    placeholder="(555) 555-5555"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  value={newGuest.email}
                  onChange={(e) => setNewGuest({...newGuest, email: e.target.value})}
                  placeholder="guest@example.com"
                />
              </div>
              <div className="form-group">
                <label>Street Address</label>
                <input
                  type="text"
                  value={newGuest.street}
                  onChange={(e) => setNewGuest({...newGuest, street: e.target.value})}
                  placeholder="123 Main Street"
                />
              </div>
              <div className="form-row-3">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    value={newGuest.city}
                    onChange={(e) => setNewGuest({...newGuest, city: e.target.value})}
                    placeholder="City"
                  />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input
                    type="text"
                    value={newGuest.state}
                    onChange={(e) => setNewGuest({...newGuest, state: e.target.value.toUpperCase()})}
                    placeholder="NY"
                    maxLength="2"
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>
                <div className="form-group">
                  <label>Zip Code</label>
                  <input
                    type="text"
                    value={newGuest.zip}
                    onChange={(e) => setNewGuest({...newGuest, zip: e.target.value})}
                    placeholder="12345"
                    maxLength="10"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Emergency Contact Name</label>
                  <input
                    type="text"
                    value={newGuest.emergencyContactName}
                    onChange={(e) => setNewGuest({...newGuest, emergencyContactName: e.target.value})}
                    placeholder="Contact name"
                  />
                </div>
                <div className="form-group">
                  <label>Emergency Contact Phone</label>
                  <input
                    type="tel"
                    value={newGuest.emergencyContactPhone}
                    onChange={(e) => setNewGuest({...newGuest, emergencyContactPhone: formatPhoneInput(e.target.value)})}
                    placeholder="(555) 555-5555"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Guest Photo (Optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={async (e) => {
                    if (e.target.files && e.target.files[0]) {
                      await acceptNewGuestPhotoFile(e.target.files[0]);
                      e.target.value = null;
                    }
                  }}
                  style={{ display: 'none' }}
                  id="new-guest-photo-input"
                />
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
                  <label
                    htmlFor="new-guest-photo-input"
                    className="btn btn-secondary"
                    onClick={handleCapturePhotoClick}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', margin: '0', cursor: 'pointer', color: 'white', width: 'auto' }}
                  >
                    <span style={{ fontSize: '18px', lineHeight: '1' }}>📷</span>
                    <span>{newGuestPhoto ? 'Photo Captured' : 'Capture Photo'}</span>
                  </label>
                  {newGuestPhoto && (
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => setNewGuestPhoto(null)}
                      style={{ padding: '10px 20px' }}
                    >
                      Remove Photo
                    </button>
                  )}
                </div>
                {newGuestPhotoUrl && (
                  <div className="new-guest-photo-preview">
                    <img src={newGuestPhotoUrl} alt="Captured guest preview" />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Check-In Form — hidden when the selected guest is already checked in
            (the banner offers Go to Check-Out instead, preventing a duplicate) */}
        {((selectedGuest && !selectedGuestCheckIn) || showNewGuestForm) && (
          <form onSubmit={handleCheckIn} className="checkin-form">
            <div className="section">
              <h2>Service Information</h2>

              <div className="form-row">
                <div className="form-group">
                  <label>Service Type *</label>
                  <select
                    value={serviceType}
                    onChange={(e) => setServiceType(e.target.value)}
                    required
                    className="service-select"
                  >
                    <option value="">Select service...</option>
                    {serviceTypes.map(service => (
                      <option key={service.id} value={service.id}>
                        {service.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Pager Number (Optional)</label>
                  <select
                    value={pagerNumber}
                    onChange={(e) => setPagerNumber(e.target.value)}
                    className="pager-input"
                  >
                    <option value="">No pager assigned</option>
                    {availablePagers.map(num => (
                      <option key={num} value={num}>Pager #{num}</option>
                    ))}
                  </select>
                  {availablePagers.length === 0 && (
                    <div className="pager-warning">No pagers available. All pagers are either assigned or missing.</div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Expected Pickup Time (Optional)</label>
                <input
                  type="time"
                  value={expectedPickupTime}
                  onChange={(e) => {
                    setExpectedPickupTime(e.target.value);
                    expectedPickupAutoFilledRef.current = false;
                  }}
                  onFocus={() => {
                    if (!expectedPickupTime) {
                      const now = new Date();
                      const hh = String(now.getHours()).padStart(2, '0');
                      const mm = String(now.getMinutes()).padStart(2, '0');
                      setExpectedPickupTime(`${hh}:${mm}`);
                      expectedPickupAutoFilledRef.current = true;
                    }
                  }}
                  onBlur={() => {
                    if (expectedPickupAutoFilledRef.current) {
                      setExpectedPickupTime('');
                    }
                    expectedPickupAutoFilledRef.current = false;
                  }}
                  className="pager-input"
                />
              </div>

              <div className="form-group">
                <label>Notes (Optional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any additional notes..."
                  rows="3"
                  className="notes-textarea"
                />
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => {
                  setSelectedGuest(null);
                  setShowNewGuestForm(false);
                  resetNewGuestForm();
                  setPagerNumber('');
                  setExpectedPickupTime('');
                  setNotes('');
                  setSearchTerm('');
                }}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting || ro.disabled}
                title={ro.title}
              >
                {submitting ? 'Checking In...' : 'Check In Guest'}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Photo Capture Modal */}
      {showPhotoCapture && (
        <div className="modal-overlay" onClick={() => setShowPhotoCapture(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Manage Photo for {selectedGuestForPhoto?.firstName} {selectedGuestForPhoto?.lastName}</h3>
              <button
                className="modal-close-btn"
                onClick={() => setShowPhotoCapture(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <p className="help-text-center">
                Take a photo or select one from your device
              </p>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    handlePhotoCapture(e.target.files[0]);
                  }
                }}
                style={{ display: 'none' }}
                id="photo-input"
              />
              {ro.disabled ? (
                <p className="help-text-center">{ro.title}</p>
              ) : (
              <label htmlFor="photo-input" className="btn btn-primary" style={{ display: 'block', margin: '0 auto 10px', maxWidth: '200px', textAlign: 'center', cursor: 'pointer', fontSize: '16px' }}>
                <span style={{ fontSize: '20px' }}>📷</span> {selectedGuestForPhoto?.image ? 'Update Photo' : 'Capture Photo'}
              </label>
              )}

              {!ro.disabled && selectedGuestForPhoto?.imageSource === 'app' && selectedGuestForPhoto?.image && (
                <button
                  className="btn btn-outline"
                  onClick={() => setShowDeleteConfirm(true)}
                  style={{ display: 'block', margin: '0 auto', maxWidth: '200px', color: '#dc3545', borderColor: '#dc3545', fontSize: '16px' }}
                >
                  <span style={{ fontSize: '20px' }}>🗑️</span> Delete Photo
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h3>Confirm Delete</h3>
              <button
                className="modal-close-btn"
                onClick={() => setShowDeleteConfirm(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <p style={{ textAlign: 'center', marginBottom: '20px' }}>
                Are you sure you want to remove the photo for {selectedGuestForPhoto?.firstName} {selectedGuestForPhoto?.lastName}?
              </p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <button
                  className="btn btn-outline"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleRemovePhoto}
                  disabled={ro.disabled}
                  title={ro.title}
                  style={{ backgroundColor: '#dc3545', borderColor: '#dc3545' }}
                >
                  Delete Photo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sys-admin Delete Check-In Confirm — for cleaning up test records */}
      {deleteCheckInTarget && (
        <div
          className="confirm-dialog-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => !deleteInProgress && setDeleteCheckInTarget(null)}
        >
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="confirm-dialog-title">Delete check-in record?</h3>
            <div className="confirm-dialog-body">
              This will permanently remove today's{' '}
              {deleteCheckInTarget.status === 'checked_out' ? 'completed' : 'active'}{' '}
              check-in for <strong>{deleteCheckInTarget.guestName}</strong>. Use this
              only for cleaning up test records.
            </div>
            <div className="confirm-dialog-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setDeleteCheckInTarget(null)}
                disabled={deleteInProgress}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeleteCheckIn}
                disabled={deleteInProgress || ro.disabled}
                title={ro.title}
              >
                {deleteInProgress ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Check-In Success Modal — OK resets the form for the next guest */}
      {showCheckedInConfirm && (
        <div
          className="confirm-dialog-overlay"
          role="dialog"
          aria-modal="true"
          onClick={dismissCheckedInConfirm}
        >
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="confirm-dialog-title">Guest checked in</h3>
            <div className="confirm-dialog-body">
              <strong>{checkedInName}</strong> has been successfully checked in.
            </div>
            <div className="confirm-dialog-actions">
              <button
                type="button"
                className="btn btn-primary"
                autoFocus
                onClick={dismissCheckedInConfirm}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Guest Confirmation — blocks silently creating a duplicate
          profile when the new-guest name matches existing participant(s) */}
      {showDuplicateConfirm && (
        <div
          className="confirm-dialog-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => { setShowDuplicateConfirm(false); setDuplicateMatches([]); }}
        >
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="confirm-dialog-title">Possible duplicate guest</h3>
            <div className="confirm-dialog-body">
              {duplicateMatches.length === 1
                ? <>A guest named <strong>{duplicateMatches[0].firstName} {duplicateMatches[0].lastName}</strong> already exists.</>
                : <><strong>{duplicateMatches.length}</strong> guests with this name already exist.</>}
              {' '}Use the existing profile instead of creating a new one?
              <div className="duplicate-warning-list" style={{ marginTop: '14px' }}>
                {duplicateMatches.map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    className="duplicate-match-btn"
                    onClick={() => handleUseExistingGuest(g)}
                  >
                    <img
                      src={g.image && !isAvatarPlaceholder(g) ? g.image : placeholderAvatar}
                      alt={`${g.firstName} ${g.lastName}`}
                      className="duplicate-match-img"
                      onLoad={(e) => handleImageLoad(g, e)}
                    />
                    <span className="duplicate-match-name">{g.firstName} {g.lastName}</span>
                    {checkedInGuestIds.has(g.id) && (
                      <span className="dropdown-checkin-badge">Checked In</span>
                    )}
                    <span className="duplicate-match-cta">Use this profile →</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="confirm-dialog-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => { setShowDuplicateConfirm(false); setDuplicateMatches([]); }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={proceedWithDuplicateCreate}
                disabled={submitting}
              >
                {submitting ? 'Creating...' : 'Create new anyway'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Guest Modal */}
      {showEditModal && selectedGuest && (
        <EditGuestModal
          guest={selectedGuest}
          onClose={() => setShowEditModal(false)}
          onSave={() => {
            setShowEditModal(false);
            loadGuests(); // Refresh guest list after edit
          }}
        />
      )}

      {/* Webcam capture (desktop) for the new-guest form */}
      <WebcamCaptureModal
        open={showWebcamCapture}
        onClose={() => setShowWebcamCapture(false)}
        onCapture={(file) => {
          acceptNewGuestPhotoFile(file);
        }}
      />

    </div>
  );
};

export default CheckIn;
